import { useRef, useState } from 'react';
import { PrimaryButton, TextButton } from '../components/Button';
import { Field } from '../components/Field';
import { Screen } from '../components/Screen';
import { Wordmark } from '../components/Wordmark';
import { copy } from '../copy';
import { useAppStore } from '../state/store';

const FORM_ID = 'login-form';

/** B1/B2 — Inloggen. Werkt nooit offline: dat zou een gevonden telefoon toegang geven. */
export function LoginScreen() {
  const online = useAppStore((s) => s.online);
  const loggingIn = useAppStore((s) => s.loggingIn);
  const loginError = useAppStore((s) => s.loginError);
  const login = useAppStore((s) => s.login);

  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [visible, setVisible] = useState(false);

  const errorText =
    loginError === 'noRole'
      ? copy.login.noDriverRole
      : loginError === 'invalid'
        ? copy.login.invalid
        : null;

  // Uit staat de knop alleen als de app het echt niet kan: zonder verbinding of tijdens het
  // inloggen. Voor lege velden is er geen copy — dan zet de knop de cursor in het lege veld.
  const disabledReason = !online ? copy.login.offline : loggingIn ? copy.login.submitting : null;

  const submit = () => {
    if (disabledReason) return;
    if (email.trim().length === 0) {
      emailRef.current?.focus();
      return;
    }
    if (password.length === 0) {
      passwordRef.current?.focus();
      return;
    }
    void login(email.trim(), password);
  };

  return (
    <Screen
      name="login"
      foot={
        <>
          <PrimaryButton
            type="submit"
            form={FORM_ID}
            disabledReason={disabledReason}
            onClick={submit}
          >
            {loggingIn ? copy.login.submitting : copy.login.submit}
          </PrimaryButton>
          <TextButton center>{copy.login.forgot}</TextButton>
        </>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <div className="stack" style={{ padding: '64px 28px 0', gap: 8 }}>
          <Wordmark />
          <h1 className="t-serif" style={{ fontSize: 34 }}>
            {copy.login.title}
          </h1>
          <p className="o-65" style={{ fontSize: 16, margin: 0 }}>
            {copy.login.intro}
          </p>
        </div>

        <div className="stack pad-x" style={{ paddingTop: 32, gap: 16 }}>
          <Field
            ref={emailRef}
            label={copy.login.email}
            type="email"
            name="email"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            value={email}
            readOnly={loggingIn}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Field
            ref={passwordRef}
            label={copy.login.password}
            type={visible ? 'text' : 'password'}
            name="password"
            autoComplete="current-password"
            value={password}
            readOnly={loggingIn}
            onChange={(event) => setPassword(event.target.value)}
            trailing={
              <TextButton onClick={() => setVisible((v) => !v)}>
                {visible ? copy.login.hide : copy.login.show}
              </TextButton>
            }
          />
          {errorText ? (
            <p className="t-body o-75" style={{ margin: 0 }} role="alert">
              {errorText}
            </p>
          ) : null}
        </div>
      </form>
    </Screen>
  );
}
